import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import OrderSourceTransactionAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/process/order/order-source-transaction/order-source-transaction.entity';
import { DocumentTypeEnum } from '../enums/document-types.enum';


export interface DocumentNavigation {
  route: string;
  param: string;
  label: string;
}

export interface NavigationResult {
  canNavigate: boolean;
  tooltip: string;
  url?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentNavigationService {
  
  private readonly documentTypeRoutes: Record<string, DocumentNavigation> = {
    [DocumentTypeEnum.PEDIDO]: { 
      route: '/master/sales/orders-list', 
      param: 'orderIds', 
      label: 'Ir al pedido' 
    },
    [DocumentTypeEnum.DOCUMENTO_DE_CARGA]: { 
      route: '/master/sales/load-documents-list', 
      param: 'orderIds', 
      label: 'Ir a documento de carga' 
    },
    [DocumentTypeEnum.ENTREGA_ENTRADA]: { 
      route: '/master/sales/delivery-list', 
      param: 'deliveryIds', 
      label: 'Ir a entrega de entrada' 
    },
    [DocumentTypeEnum.FACTURA]: { 
      route: '/master/sales/invoice-list', 
      param: 'invoiceIds', 
      label: 'Ir a la factura' 
    },
    [DocumentTypeEnum.SOLICITUD_DEVOLUCION_PRODUCTO]: { 
      route: '/master/sales/request-return-list', 
      param: 'orderIds', 
      label: 'Ir a la solicitud de devolución' 
    },
    [DocumentTypeEnum.ENTREGA_SALIDA]: { 
      route: '/master/sales/delivery-list', 
      param: 'deliveryIds', 
      label: 'Ir a la entrega' 
    },
    'TRIP': { 
      route: '/monitor/principal-screen', 
      param: 'tripId', 
      label: 'Ir al viaje' 
    },
    [DocumentTypeEnum.SOLICITUD_CAMBIO_PRODUCTO]: { 
      route: '/master/sales/request-change-item-list', 
      param: 'requestChangeItemIds', 
      label: 'Ir a la solicitud de cambio de producto' 
    },
    [DocumentTypeEnum.CAMBIO_DE_PRODUCTO]: { 
      route: '/master/sales/change-item-list', 
      param: 'orderIds', 
      label: 'Ir al cambio de producto' 
    },
    [DocumentTypeEnum.DEVOLUCION_DE_PRODUCTO]: { 
      route: '/master/sales/return-list', 
      param: 'orderIds', 
      label: 'Ir a la devolución de producto' 
    },
    [DocumentTypeEnum.DOCUMENTO_MERMA]: { 
      route: '/master/sales/loss-list', 
      param: 'orderIds', 
      label: 'Ir a la mermas' 
    },
    [DocumentTypeEnum.COTIZACION]: { 
      route: '/master/sales/quotation-list', 
      param: 'quotationIds', 
      label: 'Ir a la cotización' 
    }
  };

  constructor(private router: Router) {}

  /**
   * Obtiene información de navegación para un tipo de documento
   */
  getNavigationInfo(documentType: string, documentSerial?: string): NavigationResult {
    const config = this.documentTypeRoutes[documentType];

    if (!config) {
      return {
        canNavigate: false,
        tooltip: ''
      };
    }

    if (!documentSerial) {
      return {
        canNavigate: false,
        tooltip: ''
      };
    }

    // Verificar si la navegación está deshabilitada para este tipo de documento
    const isDisabled = this.isNavigationDisabled(documentType);

    if (isDisabled) {
      return {
        canNavigate: false,
        tooltip: 'Navegación deshabilitada para entregas de entrada',
        url: undefined
      };
    }

    const queryParams = { [config.param]: documentSerial };
    const urlTree = this.router.createUrlTree([config.route], { queryParams });
    const url = window.location.origin + this.router.serializeUrl(urlTree);

    return {
      canNavigate: true,
      tooltip: config.label,
      url
    };
  }

  /**
   * Verifica si la navegación debe estar deshabilitada para un tipo de documento
   * @param documentType - Código del tipo de documento
   * @returns true si la navegación debe estar deshabilitada
   */
  private isNavigationDisabled(documentType: string): boolean {
    // Las entregas de entrada (DT004) no deben tener navegación disponible
    // ya que no tienen pantalla o ui especial a la cual navegar
    return documentType === DocumentTypeEnum.ENTREGA_ENTRADA;
  }

  /**
   * Navega a un documento específico
   */
  navigateToDocument(documentType: string, documentSerial: string | string[]): void {
    const config = this.documentTypeRoutes[documentType];

    if (!config || !documentSerial) {
      console.warn(`No se puede navegar: tipo de documento '${documentType}' no válido o serial faltante`);
      return;
    }

    // Verificar si la navegación está deshabilitada
    if (this.isNavigationDisabled(documentType)) {
      console.warn(`Navegación deshabilitada para el tipo de documento '${documentType}'`);
      return;
    }

    // Convertir array a string si es necesario
    const serialParam = Array.isArray(documentSerial) ? documentSerial.join(',') : documentSerial;

    const queryParams = { [config.param]: serialParam };
    const urlTree = this.router.createUrlTree([config.route], { queryParams });
    const absoluteUrl = window.location.origin + this.router.serializeUrl(urlTree);

    // Abrir en nueva pestaña
    window.open(absoluteUrl, '_blank');
  }

  /**
   * Verifica si un tipo de documento es navegable
   */
  isNavigable(documentType: string): boolean {
    return !!this.documentTypeRoutes[documentType];
  }

  /**
   * Obtiene el label para un tipo de documento
   */
  getLabel(documentType: string): string {
    const config = this.documentTypeRoutes[documentType];
    return config?.label || '';
  }

  /**
   * Navegación especial para entregas a pedidos (DT003 → DT002)
   */
  navigateFromDeliveryToOrders(sourceTransactions: OrderSourceTransactionAttributes[]): void {
    if (!Array.isArray(sourceTransactions)) {
      console.warn('sourceTransactions debe ser un array');
      return;
    }

    const orderIds = sourceTransactions
      .filter(t => t?.type?.code === 'DT002')
      .map(t => t.serial)
      .join(',');

    if (orderIds.length > 0) {
      this.navigateToDocument('DT002', orderIds);
    } else {
      console.warn('No se encontraron pedidos relacionados');
    }
  }

  /**
   * Obtiene todos los tipos de documento disponibles
   */
  getAvailableDocumentTypes(): string[] {
    return Object.keys(this.documentTypeRoutes);
  }
}